import React, { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/**
 * Touch- and mouse-driven signature canvas. Emits the trimmed PNG data URL
 * via `onChange` on pointer-up. Shows `value` as a preview if it's already
 * set (e.g. when resuming a saved survey).
 */
export default function SignatureCanvas({ value, onChange, disabled, testid }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(!!value);

  useEffect(() => {
    if (!ref.current) return;
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#0E1A33";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (value && value.startsWith("data:image")) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
      setHasInk(true);
    }
  }, [value]);

  const point = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: (t.clientX - rect.left) * (ref.current.width / rect.width),
             y: (t.clientY - rect.top) * (ref.current.height / rect.height) };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    const { x, y } = point(e);
    const ctx = ref.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = point(e);
    const ctx = ref.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    try {
      const dataUrl = ref.current.toDataURL("image/png");
      onChange?.(dataUrl);
    } catch (_) { /* ignore */ }
  };

  const clear = () => {
    if (!ref.current) return;
    const ctx = ref.current.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, ref.current.width, ref.current.height);
    setHasInk(false);
    onChange?.("");
  };

  return (
    <div className="space-y-1.5">
      <canvas
        ref={ref}
        width={640}
        height={180}
        data-testid={testid}
        className="w-full max-w-xl border border-slate-300 rounded-md bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex items-center gap-2">
        <button type="button" onClick={clear} disabled={disabled || !hasInk}
          className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-red-600 inline-flex items-center gap-1 disabled:opacity-50"
        ><Eraser size={11} /> Clear</button>
        {!hasInk && <span className="text-[10px] uppercase tracking-wider text-slate-400">Sign above</span>}
      </div>
    </div>
  );
}
